<!--
/*
 * Компонент AdminConsole — отображение перехваченных console-логов
 *
 * Основные функции:
 * - Показ всех console.log/warn/error/info в обратном хронологическом порядке
 * - Фильтрация по уровню лога (all / log / warn / error / info)
 * - Очистка буфера логов
 * - Моноширинный шрифт для читаемости
 */
-->
<script setup lang="ts">
// Компонент заголовка секции с кнопкой «Назад»
import SectionHeader from '@/components/SectionHeader.vue'
// Реактивный буфер и функция очистки из перехватчика console
import { consoleBuffer, clearConsoleBuffer } from '@/utils/consoleInterceptor'
// Реактивная ссылка и вычисляемое свойство Vue
import { ref, computed } from 'vue'
// Определение размера экрана — для динамического padding
import { useDisplay } from 'vuetify'
// Утилита определения Telegram-контекста
import { isTelegram } from '@/utils/isTelegram'
// Копирование в буфер обмена через vueuse
import { useClipboard } from '@vueuse/core'
// Тип записи лога
import type { IConsoleEntry } from '@/modules/admin/types/IConsoleEntry'

// Функция копирования текста в буфер обмена
const { copy, copied } = useClipboard()
// Определение мобильного экрана для динамического padding
const { mobile } = useDisplay()

/** Текущий фильтр уровня лога */
const activeFilter = ref<IConsoleEntry['level'] | 'all'>('all')

/** Доступные фильтры для чипсов */
const filters = [
  { value: 'all' as const, label: 'Все' },
  { value: 'log' as const, label: 'Log' },
  { value: 'info' as const, label: 'Info' },
  { value: 'warn' as const, label: 'Warn' },
  { value: 'error' as const, label: 'Error' },
]

/**
 * Отфильтрованные записи в обратном порядке (новые сверху)
 *
 * @returns массив записей, отфильтрованных по активному уровню
 */
const filteredEntries = computed(() => {
  // 1. Фильтруем по уровню
  const entries =
    activeFilter.value === 'all'
      ? consoleBuffer.value
      : consoleBuffer.value.filter((e) => e.level === activeFilter.value)
  // 2. Разворачиваем — новые записи сверху
  return [...entries].reverse()
})

/**
 * Форматирует timestamp в HH:mm:ss
 *
 * @param timestamp - время в миллисекундах (Date.now())
 * @returns отформатированная строка времени
 */
function formatTime(timestamp: number): string {
  const date = new Date(timestamp)
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `${hours}:${minutes}:${seconds}`
}

/**
 * Возвращает цвет чипа по уровню лога
 *
 * @param level - уровень лога
 * @returns имя цвета Vuetify-темы
 */
function levelColor(level: IConsoleEntry['level']): string {
  const colors: Record<IConsoleEntry['level'], string> = {
    log: 'default',
    info: 'info',
    warn: 'warning',
    error: 'error',
  }
  return colors[level]
}

/**
 * Копирует все отфильтрованные записи в буфер обмена
 * Формат: [HH:mm:ss] [LEVEL] текст
 */
const handleCopyAll = () => {
  const text = filteredEntries.value
    .map((e) => `[${formatTime(e.timestamp)}] [${e.level.toUpperCase()}] ${e.args}`)
    .join('\n')
  copy(text)
}
</script>

<template>
  <div class="d-flex flex-column ga-4 pt-4" :class="{ 'pa-4': isTelegram() || mobile }">
    <header>
      <section-header />
    </header>

    <!-- Панель управления: фильтры + очистка -->
    <div class="d-flex align-center ga-2 flex-wrap">
      <VChip
        v-for="filter in filters"
        :key="filter.value"
        :variant="activeFilter === filter.value ? 'flat' : 'outlined'"
        :color="activeFilter === filter.value ? 'primary' : undefined"
        size="small"
        @click="activeFilter = filter.value"
      >
        {{ filter.label }}
      </VChip>

      <v-spacer />

      <VBtn
        variant="text"
        size="small"
        :prepend-icon="copied ? 'ri-check-line' : 'ri-file-copy-line'"
        @click="handleCopyAll"
      >
        {{ copied ? 'Скопировано' : 'Копировать всё' }}
      </VBtn>

      <VBtn variant="text" size="small" prepend-icon="ri-delete-bin-line" @click="clearConsoleBuffer">
        Очистить
      </VBtn>
    </div>

    <!-- Счётчик записей -->
    <div class="text-caption text-medium-emphasis">
      Записей: {{ filteredEntries.length }} / {{ consoleBuffer.length }}
    </div>

    <!-- Список записей -->
    <div class="console-entries d-flex flex-column ga-1">
      <div
        v-for="(entry, index) in filteredEntries"
        :key="index"
        class="console-entry d-flex flex-column ga-1 rounded pa-2"
      >
        <!-- Верхняя строка: время + уровень + копирование -->
        <div class="d-flex align-center ga-2">
          <VChip :color="levelColor(entry.level)" size="x-small" variant="flat">
            {{ entry.level }}
          </VChip>
          <span class="console-time text-caption text-medium-emphasis">
            {{ formatTime(entry.timestamp) }}
          </span>
          <v-spacer />
          <VIconBtn
            :icon="copied ? 'ri-check-line' : 'ri-file-copy-line'"
            variant="text"
            size="small"
            @click="copy(entry.args)"
          />
        </div>

        <!-- Текст лога на всю ширину -->
        <span class="console-message text-body-2">{{ entry.args }}</span>
      </div>

      <!-- Пустое состояние -->
      <div v-if="filteredEntries.length === 0" class="text-center text-medium-emphasis pa-8">
        Нет записей
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Моноширинный шрифт и перенос длинных строк для текста логов */
.console-message {
  font-family: 'Courier New', Courier, monospace;
  white-space: pre-wrap;
  word-break: break-all;
}

/* Фиксированная ширина времени */
.console-time {
  font-family: 'Courier New', Courier, monospace;
  min-width: 90px;
}

/* Фон записи для визуального разделения */
.console-entry {
  background-color: rgba(var(--v-theme-surface-variant), 0.3);
}
</style>
